import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { collection, addDoc, getFirestore } from 'firebase/firestore';
import DefaultAuth from 'layouts/auth/Default';
import illustration from 'assets/img/auth/auth.png';
import { MdOutlineRemoveRedEye, MdEmail } from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';
import { AuthContext } from 'contexts/AuthContext';
import { app } from 'lib/firebase';

const db = getFirestore(app);

function SignIn() {
  const textColor = useColorModeValue('navy.700', 'white');
  const textColorSecondary = 'gray.400';
  const textColorDetails = useColorModeValue('navy.700', 'secondaryGray.600');
  const textColorBrand = useColorModeValue('brand.500', 'white');
  const textColorError = 'pink.600';
  const brandStars = useColorModeValue('brand.500', 'brand.400');

  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [authMode, setAuthMode] = React.useState('magic');
  const [loginError, setLoginError] = React.useState('');
  const [linkSent, setLinkSent] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [emailFromUrl, setEmailFromUrl] = React.useState(false);
  const [isSendingSupport, setIsSendingSupport] = React.useState(false);
  const [isSendingReset, setIsSendingReset] = React.useState(false);

  const { sendMagicLink, signIn, forgotPassword } = React.useContext(AuthContext);
  const toast = useToast();

  React.useEffect(() => {
    const hash = window.location.hash;
    const queryStart = hash.indexOf('?');
    if (queryStart === -1) return;

    const urlParams = new URLSearchParams(hash.substring(queryStart));
    const emailParam = urlParams.get('email');
    if (emailParam) {
      setEmail(emailParam);
      setEmailFromUrl(true);
    }
  }, []);

  const handleSendMagicLink = async () => {
    setLoginError('');
    setLinkSent(false);

    if (!email || !email.trim()) {
      setLoginError('Please enter your email address.');
      return;
    }

    setIsLoading(true);
    const error = await sendMagicLink(email);
    setIsLoading(false);

    if (error) {
      setLoginError(error);
    } else {
      setLinkSent(true);
    }
  };

  const handlePasswordSignIn = async () => {
    setLoginError('');

    if (!email || !email.trim()) {
      setLoginError('Please enter your email address.');
      return;
    }

    if (!password || !password.trim()) {
      setLoginError('Please enter your password.');
      return;
    }

    setIsLoading(true);
    const error = await signIn(email, password);
    setIsLoading(false);

    if (error) {
      setLoginError(error);
    }
  };

  const switchToPasswordMode = () => {
    setAuthMode('password');
    setLoginError('');
    setLinkSent(false);
  };

  const switchToMagicMode = () => {
    setAuthMode('magic');
    setLoginError('');
    setPassword('');
  };

  const handleForgotPassword = async () => {
    if (!email || !email.trim()) {
      toast({
        title: 'Email required',
        description: 'Please enter your email address first.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    setIsSendingReset(true);
    const error = await forgotPassword(email);
    setIsSendingReset(false);

    if (error) {
      setLoginError(error);
      return;
    }

    toast({
      title: 'Password reset email sent',
      description: `Check your inbox at ${email} for password reset instructions.`,
      status: 'success',
      duration: 6000,
      isClosable: true,
      position: 'top',
    });
  };

  const handleContactSupport = async () => {
    setIsSendingSupport(true);

    try {
      const html = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="background: linear-gradient(135deg, #4299e1 0%, #3182ce 100%); padding: 30px; border-radius: 8px 8px 0 0; text-align: center;">
            <h1 style="color: white; margin: 0; font-size: 28px; font-weight: 700;">Incorrect Email on Instructor Sign-In Link</h1>
          </div>
          <div style="background-color: #ffffff; padding: 30px; border: 1px solid #e2e8f0; border-top: none;">
            <p style="color: #2d3748; font-size: 16px; line-height: 1.6;">
              An instructor reported that the email pre-filled in their sign-in link is incorrect.
            </p>
            <p style="color: #2d3748; font-size: 14px;"><strong>Incorrect Email:</strong> ${email}</p>
            <p style="color: #2d3748; font-size: 14px;"><strong>Reported At:</strong> ${new Date().toLocaleString('en-US', { timeZone: 'America/New_York' })} EST</p>
            <p style="color: #2d3748; font-size: 14px;"><strong>URL:</strong> ${window.location.href}</p>
          </div>
        </div>
      `;

      await addDoc(collection(db, 'mail'), {
        to: 'support@fitgroupusa.com',
        from: 'info@fitgroupusa.com',
        message: {
          subject: 'Instructor Portal Sign-In Link - Incorrect Email Reported',
          html,
        },
      });

      toast({
        title: 'Support contacted',
        description: 'We notified the support team. They will reach out shortly.',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to contact support. Please email support@fitgroupusa.com directly.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsSendingSupport(false);
    }
  };

  return (
    <DefaultAuth illustrationBackground={illustration} image={illustration}>
      <Flex
        maxW={{ base: '100%', md: 'max-content' }}
        w="100%"
        mx={{ base: 'auto', lg: '0px' }}
        me="auto"
        h="100%"
        alignItems="start"
        justifyContent="center"
        mb={{ base: '30px', md: '60px' }}
        px={{ base: '25px', md: '0px' }}
        mt={{ base: '40px', md: '14vh' }}
        flexDirection="column"
      >
        <Box me="auto">
          <Flex mb="24px" bg="gray.50" borderRadius="12px" p="4px" w="fit-content">
            <Button
              fontSize="sm"
              fontWeight="500"
              px="20px"
              py="8px"
              bg="brand.500"
              color="white"
              borderRadius="8px"
              _hover={{ bg: 'brand.500' }}
              _active={{ bg: 'brand.500' }}
            >
              I'm an Instructor
            </Button>
            <Button
              fontSize="sm"
              fontWeight="500"
              px="20px"
              py="8px"
              bg="transparent"
              color="gray.700"
              borderRadius="8px"
              _hover={{ bg: 'gray.100' }}
              onClick={() => { window.location.href = 'https://facility.fitgroupusa.com/'; }}
            >
              I'm a Facility
            </Button>
          </Flex>

          <Heading color={textColor} fontSize="36px" mb="10px">
            {emailFromUrl ? 'Welcome!' : 'Sign In'}
          </Heading>
          <Text mb="36px" ms="4px" color={textColorSecondary} fontWeight="400" fontSize="md">
            {authMode === 'magic'
              ? (emailFromUrl
                ? "Click the button below and we'll send you a secure sign-in link."
                : "Enter your email and we'll send you a secure sign-in link.")
              : 'Enter your email and password to sign in.'}
          </Text>
        </Box>

        <Flex
          zIndex="2"
          direction="column"
          w={{ base: '100%', md: '420px' }}
          maxW="100%"
          background="transparent"
          borderRadius="15px"
          mx={{ base: 'auto', lg: 'unset' }}
          me="auto"
          mb={{ base: '20px', md: 'auto' }}
        >
          <FormControl>
            <FormLabel display="flex" ms="4px" fontSize="sm" fontWeight="500" color={textColor} mb="8px">
              Email<Text color={brandStars}>*</Text>
            </FormLabel>
            {emailFromUrl && (
              <Text mb="8px" ms="4px" color={textColorSecondary} fontSize="xs">
                This email has been pre-filled for you
              </Text>
            )}
            <Input
              isRequired
              variant="auth"
              fontSize="sm"
              type="email"
              placeholder="instructor@example.com"
              mb={emailFromUrl ? '12px' : '24px'}
              fontWeight="500"
              size="lg"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  if (authMode === 'magic') handleSendMagicLink();
                  if (authMode === 'password' && password) handlePasswordSignIn();
                }
              }}
              isDisabled={emailFromUrl}
              opacity={emailFromUrl ? 0.7 : 1}
              cursor={emailFromUrl ? 'not-allowed' : 'text'}
            />

            {emailFromUrl && (
              <Flex mb="24px" justifyContent="center">
                <Text fontSize="sm" color={textColorSecondary}>
                  Not the right email?{' '}
                  <Link
                    color={textColorBrand}
                    fontWeight="500"
                    onClick={handleContactSupport}
                    cursor="pointer"
                    textDecoration="underline"
                    isDisabled={isSendingSupport}
                  >
                    {isSendingSupport ? 'Contacting support...' : 'Contact support'}
                  </Link>
                </Text>
              </Flex>
            )}

            {authMode === 'password' && (
              <FormControl mb="24px">
                <FormLabel ms="4px" fontSize="sm" fontWeight="500" color={textColor} display="flex">
                  Password<Text color={brandStars}>*</Text>
                </FormLabel>
                <InputGroup size="md">
                  <Input
                    isRequired
                    fontSize="sm"
                    placeholder="Enter your password"
                    size="lg"
                    type={showPassword ? 'text' : 'password'}
                    variant="auth"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && password) handlePasswordSignIn();
                    }}
                  />
                  <InputRightElement display="flex" alignItems="center" mt="4px">
                    <Icon
                      color={textColorSecondary}
                      _hover={{ cursor: 'pointer' }}
                      as={showPassword ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                      onClick={() => setShowPassword(!showPassword)}
                    />
                  </InputRightElement>
                </InputGroup>
                <Flex justifyContent="flex-end" mt="8px">
                  <Link
                    fontSize="sm"
                    color={textColorBrand}
                    fontWeight="500"
                    onClick={handleForgotPassword}
                    cursor="pointer"
                    isDisabled={isSendingReset}
                    _hover={{ textDecoration: 'underline' }}
                  >
                    {isSendingReset ? 'Sending...' : 'Forgot password?'}
                  </Link>
                </Flex>
              </FormControl>
            )}

            {linkSent && authMode === 'magic' && (
              <Box mb="24px" p="16px" borderRadius="12px" bg="green.50" borderLeft="4px solid" borderColor="green.500">
                <Text color="green.700" fontWeight="500" fontSize="sm">
                  Check your email!
                </Text>
                <Text color="green.600" fontSize="sm" mt="8px">
                  We've sent a sign-in link to <strong>{email}</strong>. Click the link in the email to sign in.
                </Text>
                <Text color="green.600" fontSize="xs" mt="8px">
                  Make sure to complete the sign-in on the same device where you requested it.
                </Text>
                <Text color="orange.600" fontWeight="600" fontSize="xs" mt="8px" bg="orange.50" p="8px" borderRadius="6px">
                  Don't see it? Check your spam folder for a message starting with "Sign in to FitGroupUSA requested at"
                </Text>
              </Box>
            )}

            {authMode === 'magic' && (
              <>
                <Button
                  fontSize="sm"
                  variant="brand"
                  fontWeight="500"
                  w="100%"
                  h="50"
                  mb="12px"
                  onClick={handleSendMagicLink}
                  isLoading={isLoading}
                  loadingText="Sending..."
                  leftIcon={<Icon as={MdEmail} w="20px" h="20px" />}
                >
                  Send Magic Link
                </Button>
                <Button
                  fontSize="sm"
                  variant="outline"
                  fontWeight="500"
                  w="100%"
                  h="50"
                  mb="24px"
                  onClick={switchToPasswordMode}
                  borderColor="gray.300"
                  color={textColor}
                  _hover={{ bg: 'gray.50' }}
                >
                  Sign in with Password
                </Button>
              </>
            )}

            {authMode === 'password' && (
              <>
                <Button
                  fontSize="sm"
                  variant="brand"
                  fontWeight="500"
                  w="100%"
                  h="50"
                  mb="12px"
                  onClick={handlePasswordSignIn}
                  isLoading={isLoading}
                  loadingText="Signing in..."
                >
                  Sign In
                </Button>
                <Button
                  fontSize="sm"
                  variant="ghost"
                  fontWeight="500"
                  w="100%"
                  h="50"
                  mb="24px"
                  onClick={switchToMagicMode}
                  color={textColorBrand}
                >
                  Back to Magic Link
                </Button>
              </>
            )}
          </FormControl>

          {loginError && (
            <Flex mb="24px">
              <Text color={textColorError} fontSize="sm" fontWeight="500">
                {loginError}
              </Text>
            </Flex>
          )}

          <Flex flexDirection="column" justifyContent="center" alignItems="start" maxW="100%" mt="0px">
            <Text color={textColorDetails} fontWeight="400" fontSize="14px">
              Not registered yet?
              <NavLink to="/auth/sign-up">
                <Text color={textColorBrand} as="span" ms="5px" fontWeight="500">
                  Create an Account
                </Text>
              </NavLink>
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </DefaultAuth>
  );
}

export default SignIn;
